import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {useHistory, useLocation} from "react-router-dom";
import * as elementsActions from "../../../../redux/actions/elements";
import * as userActions from "../../../../redux/actions/user";
import * as loyaltyRepo from "../../../../shared/repos/graphql/loyalty";
import { PUNCHH } from "../../../../shared/constants/loyaltyType";
import amplitudeAnalytics, { AMPLITUDE_NAV_SECTION } from "../../../../shared/utilities/amplitude";
import siteSetting from "../../../../shared/constants/siteSetting";
import permissionTypes from "../../../../shared/constants/permissionTypes";
import { hasPermission } from "../../../../shared/utilities/common";
import { isExternalPath } from "../../../../shared/utilities/pathes";
import Switch from "../../../Switch/Switch";
import styles from "./FullScreenHomepageHamburgerMenu.module.css";
import "./FullScreenHomepageHamburgerMenuBrandRefresh.module.css";
import {
  getHamburgerIcon,
  getHamburgerPartProps,
  getHamburgerRegionProps,
  isHamburgerMenuBrandRefresh,
} from "./hamburgerMenuBrandRefreshClasses";

import packsIcon from "./imgs/Product_Photos_Packs.png";
import cookiesIcon from "./imgs/Product_Photos_Cookies.png";
import iceCreamIcon from "./imgs/Product_Photos_Ice_Cream.png";
import cakesIcon from "./imgs/Product_Photos_Cakes.png";
import tiktokIcon from "./imgs/TiktokLogo.svg";
import instagramIcon from "./imgs/InstagramLogo.svg";
import facebookIcon from "./imgs/FacebookLogo.svg";
import accessibilityIcon from "./imgs/Wheelchair.svg";
import cookieIcon from "./imgs/Cookie.svg";
import brandRefreshTiktokIcon from "./imgs/brand-refresh-TiktokLogo.svg";
import brandRefreshInstagramIcon from "./imgs/brand-refresh-InstagramLogo.svg";
import brandRefreshFacebookIcon from "./imgs/brand-refresh-FacebookLogo.svg";
import brandRefreshAccessibilityIcon from "./imgs/brand-refresh-Wheelchair.svg";
import brandRefreshCookieIcon from "./imgs/brand-refresh-Cookie.svg";
import legacyPlusIcon from "./imgs/Plus.svg";
import brandRefreshPlusIcon from "./imgs/brand-refresh-Plus.svg";
import legacyMinusIcon from "./imgs/Minus.svg";
import brandRefreshMinusIcon from "./imgs/brand-refresh-Minus.svg";
import barcodeIcon from "./imgs/Barcode.svg";
import legacyUserCircleIcon from "./imgs/UserCircle.svg";
import legacyRewardsIcon from "./imgs/RewardsIcon.svg";
import legacyOrderReceiptIcon from "./imgs/OrderReceiptIcon.svg";
import brandRefreshUserCircleIcon from "./imgs/brand-refresh-UserCircle.svg";
import brandRefreshRewardsIcon from "./imgs/brand-refresh-RewardsIcon.svg";
import brandRefreshOrderReceiptIcon from "./imgs/brand-refresh-OrderReceiptIcon.svg";
import legacyBadgeIcon from "./imgs/Badge.svg";
import brandRefreshBadgeIcon from "./imgs/brand-refresh-Badge.svg";
import legacyAppDownloadIcon from "./imgs/AppDownload.png";
import brandRefreshAppDownloadIcon from "./imgs/brand-refresh-AppDownload-new.svg";
import endpoints from "../../../../shared/constants/endpoints";

const HamburgerMenu = ({
  toggleMobileMenu,
  toggleBarcodeCard,
  userToken,
  permissions,
  cmsEditMode,
  setCmsEditMode,
  activeLoyalty,
  footerMenuItemsSettings,
  footerMenuItemsLinksSettings,
  hasActiveOrders,
  selectedCountry,
}) => {
  const isAuthenticated = !!userToken;
  const isBrandRefresh = isHamburgerMenuBrandRefresh();
  const plusIcon = getHamburgerIcon(legacyPlusIcon, brandRefreshPlusIcon);
  const minusIcon = getHamburgerIcon(legacyMinusIcon, brandRefreshMinusIcon);
  const userCircleIcon = getHamburgerIcon(
    legacyUserCircleIcon,
    brandRefreshUserCircleIcon,
  );
  const rewardsIcon = getHamburgerIcon(
    legacyRewardsIcon,
    brandRefreshRewardsIcon,
  );
  const ordersIcon = getHamburgerIcon(
    legacyOrderReceiptIcon,
    brandRefreshOrderReceiptIcon,
  );
  const ordersBadgeIcon = getHamburgerIcon(
    legacyBadgeIcon,
    brandRefreshBadgeIcon,
  );
  const appDownloadIcon = getHamburgerIcon(
    legacyAppDownloadIcon,
    brandRefreshAppDownloadIcon,
  );
  const socialTiktokIcon = getHamburgerIcon(tiktokIcon, brandRefreshTiktokIcon);
  const socialInstagramIcon = getHamburgerIcon(
    instagramIcon,
    brandRefreshInstagramIcon,
  );
  const socialFacebookIcon = getHamburgerIcon(
    facebookIcon,
    brandRefreshFacebookIcon,
  );
  const socialAccessibilityIcon = getHamburgerIcon(
    accessibilityIcon,
    brandRefreshAccessibilityIcon,
  );
  const socialCookieIcon = getHamburgerIcon(cookieIcon, brandRefreshCookieIcon);
  const canEditCms =
    isAuthenticated &&
    hasPermission(
      permissions,
      permissionTypes.cms.category,
      permissionTypes.cms.permissions.edit_values,
    );
  const [isMoreExpanded, setIsMoreExpanded] = useState(false);
  const [animationState, setAnimationState] = useState("opening");
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 768;
  const location = useLocation();
  const history = useHistory();

  const trackNavClick = (navElement) => {
    amplitudeAnalytics.trackClickNavPrimary({
      navElement,
      navSection: AMPLITUDE_NAV_SECTION.HAMBURGER_MENU,
      pathname: location.pathname,
    });
  };

  const handleScanBarcode = (e) => {
    e.preventDefault();
    trackNavClick("Scan Barcode");
    toggleMobileMenu();
    toggleBarcodeCard({
      showBarcodeCard: true,
      value: null,
    });
    loyaltyRepo.getLoyaltyProfile().then((response) => {
      toggleBarcodeCard({
        showBarcodeCard: true,
        value: response.data?.loyaltyProfile?.qrCode,
      });
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationState("open");
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setAnimationState("closing");
    setTimeout(() => {
      toggleMobileMenu();
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const getOverlayClassName = () => {
    let className = styles.menuOverlay;
    if (animationState === "closing") {
      className += ` ${styles.closing}`;
    }
    if (animationState === "opening" || animationState === "open") {
      className += ` ${styles.visible}`;
    }
    return className;
  };

  const getContainerClassName = () => {
    let className = styles.menuContainer;
    if (animationState === "opening" || animationState === "open") {
      className += ` ${styles.visible}`;
    }
    if (animationState === "closing") {
      className += ` ${styles.closing}`;
    }
    return className;
  };

  const getLink = (key) => {
    return footerMenuItemsLinksSettings?.[key] || "";
  };

  const isEnabled = (key) => {
    return footerMenuItemsSettings?.[key] || false;
  };

  const handleNavigation = (path, anchor = null) => {
    const targetPath = anchor ? `${path}${anchor}` : path;
    toggleMobileMenu();
    history.push(targetPath);
  };

  const menuItems = [
    { name: "Packs", icon: packsIcon, anchor: "#packs" },
    { name: "Cookies", icon: cookiesIcon, anchor: "#cookies" },
    { name: "Ice Cream", icon: iceCreamIcon, anchor: "#ice-cream" },
    { name: "Cakes & More", icon: cakesIcon, anchor: "#cakes" },
  ];

  const authItems = [
    { name: "Orders", icon: ordersIcon, path: endpoints.orders },
    { name: "Profile", icon: userCircleIcon, path: endpoints.profilePage },
    { name: "Rewards", icon: rewardsIcon, path: endpoints.loyalty },
  ];

  const brandRefreshNavItems = [
    { name: "Find a Bakery", path: "/findaninsomnia" },
    { name: "Gifting", path: "/gifting" },
    { name: "Shipping", path: "/shipping" },
    { name: "Contact", path: "/contact" },
    ...(!isAuthenticated
      ? [{ name: "Insomnia Rewards", path: "/insomniarewards" }]
      : []),
    { name: "Careers", path: "https://careers.insomniacookies.com/" },
    {
      name: "Gift Cards",
      path: "https://wwws-usa2.givex.com/cws4.0/insomniacookies/",
    },
    { name: "More", path: "/more", suffix: "+", isExpandable: true },
  ];

  const navItems = isBrandRefresh
    ? brandRefreshNavItems
    : [
        { name: "Find a Bakery", path: "/findaninsomnia" },
        { name: "Gifting", path: "/gifting" },
        { name: "Shipping", path: "/shipping" },
        ...(!isAuthenticated
          ? [{ name: "Insomnia Rewards", path: "/insomniarewards" }]
          : []),
        { name: "Careers", path: "https://careers.insomniacookies.com/" },
        {
          name: "Gift Cards",
          path: "https://wwws-usa2.givex.com/cws4.0/insomniacookies/",
        },
        { name: "Contact", path: "/contact" },
        { name: "More", path: "/more", suffix: "+", isExpandable: true },
      ];

  const moreItems = [
    ...(isEnabled(siteSetting.IS_FOOTER_MENU_NEWSROOM_ENABLED)
      ? [
          {
            name: "Newsroom",
            path: getLink(siteSetting.FOOTER_MENU_NEWSROOM_LINK),
          },
        ]
      : []),
    ...(isEnabled(siteSetting.IS_FOOTER_MENU_CORPORATE_SALES_ENABLED)
      ? [
          {
            name: "Corporate Sales",
            path: getLink(siteSetting.FOOTER_MENU_CORPORATE_SALES_LINK),
          },
        ]
      : []),
    ...(isEnabled(siteSetting.IS_FOOTER_MENU_PRIVACY_POLICY_ENABLED)
      ? [
          {
            name: "Privacy Policy",
            path: getLink(siteSetting.FOOTER_MENU_PRIVACY_POLICY_LINK),
          },
        ]
      : []),
    { name: "Terms & Conditions", path: "/terms" },
    { name: "Accessibility", path: "/accessibility" },
    { name: "About us", path: "/about" },
    ...(isEnabled(siteSetting.IS_FOOTER_MENU_ALLERGEN_ENABLED)
      ? [
          {
            name: "Allergen & Nutrition",
            path: getLink(siteSetting.FOOTER_MENU_ALLERGEN_LINK),
          },
        ]
      : []),
    ...(isEnabled(siteSetting.IS_FOOTER_MENU_DONATIONS_ENABLED)
      ? [
          {
            name: "Donations",
            path: getLink(siteSetting.FOOTER_MENU_DONATIONS_LINK),
          },
        ]
      : []),
    {
      name: `Countries${
        selectedCountry?.["short_name"]
          ? ` (${selectedCountry.short_name})`
          : ""
      }`,
      path: "/countries",
    },
  ];

  const handleMoreClick = () => {
    setIsMoreExpanded(!isMoreExpanded);
  };

  const handleCookiesClick = () => {
    if (window.Cookiebot) {
      window.Cookiebot.renew();
    }
  };

  const handleSocialIconClick = (social) => {
    if (social.isCookies) {
      toggleMobileMenu();
      setTimeout(handleCookiesClick, 350);
    }
    if (social.isAccessibility) {
      toggleMobileMenu();
      setTimeout(() => {
        document.getElementById("userwayAccessibilityIcon").click();
      }, 350);
    }
  };

  const socialIcons = [
    {
      name: "TikTok",
      icon: socialTiktokIcon,
      path: "https://www.tiktok.com/@insomniacookies",
    },
    {
      name: "Instagram",
      icon: socialInstagramIcon,
      path: "https://www.instagram.com/insomniacookies",
    },
    {
      name: "Facebook",
      icon: socialFacebookIcon,
      path: "https://www.facebook.com/InsomniaCookies",
    },
    {
      name: "Accessibility",
      icon: socialAccessibilityIcon,
      isAccessibility: true,
    },
    { name: "Cookies", icon: socialCookieIcon, isCookies: true },
  ];

  const currentYear = new Date().getFullYear();

  const signInBlock = !isAuthenticated ? (
    <div
      className={styles.signInSection}
      data-part={getHamburgerPartProps("sign-in-section")}
    >
      {isBrandRefresh ? (
        <div
          className={styles.signInContent}
          data-part={getHamburgerPartProps("sign-in-content")}
        >
          <button
            type="button"
            className={styles.signInButton}
            data-part={getHamburgerPartProps("sign-in-button")}
            onClick={() => {
              trackNavClick("Sign In");
              handleNavigation("/login");
            }}
          >
            Sign In
          </button>
          <a
            href="/create-account"
            className={styles.rewardsText}
            data-part={getHamburgerPartProps("rewards-text")}
            onClick={() => trackNavClick("Join Rewards")}
          >
            Join Rewards and get
            {" "}
            <span
              className={styles.rewardsTextFree}
              data-part={getHamburgerPartProps("rewards-text-free")}
            >
              FREE
            </span>
            {' '}
treats!
          </a>
        </div>
      ) : (
        <>
          <button
            type="button"
            className={styles.signInButton}
            onClick={() => {
              trackNavClick("Sign In");
              handleNavigation("/login");
            }}
          >
            Sign In
          </button>
          <a
            href="/create-account"
            className={styles.rewardsText}
            onClick={() => trackNavClick("Join Rewards")}
          >
            Join Rewards and get
            {" "}
            <span className={styles.rewardsTextFree}>
              FREE
            </span>
            {' '}
treats!
          </a>
        </>
      )}
    </div>
  ) : null;

  const menuBody = (
    <>
      {/* Close Button */}
      <button
        type="button"
        className={styles.closeButton}
        data-part={getHamburgerPartProps("close-button")}
        onClick={handleClose}
        aria-label="Close menu"
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {signInBlock}

      {!isAuthenticated && !isBrandRefresh && (
      <div className={styles.divider} />
        )}

      {/* Menu Items */}
      <div
        className={styles.menuItems}
        data-part={getHamburgerPartProps("menu-items")}
        data-auth-state={
            isBrandRefresh && isAuthenticated ? "signed-in" : undefined
          }
      >
        {menuItems.map((item) => (
          <button
            type="button"
            key={item.name}
            className={styles.menuItem}
            data-part={getHamburgerPartProps("menu-item")}
            onClick={() => {
                trackNavClick(item.name);
                handleNavigation("/menu", item.anchor);
              }}
          >
            {item.icon && (
            <span
              className={styles.menuItemIcon}
              data-part={getHamburgerPartProps("menu-item-icon")}
            >
              <img src={item.icon} alt="" />
            </span>
              )}
            <span
              className={styles.menuItemText}
              data-part={getHamburgerPartProps("menu-item-text")}
            >
              {item.name}
            </span>
          </button>
          ))}
      </div>

      {/* View Full Menu Button */}
      <div
        className={styles.viewMenuButtonContainer}
        data-part={getHamburgerPartProps("view-menu-container")}
      >
        <button
          type="button"
          className={styles.viewMenuButton}
          data-part={getHamburgerPartProps("view-menu-button")}
          onClick={() => {
              trackNavClick("View Full Menu");
              handleNavigation("/menu");
            }}
        >
            View Full Menu
        </button>
      </div>

      {isAuthenticated && (
      <>
        <div
          className={styles.divider}
          data-part={getHamburgerPartProps("divider")}
        />
        <div
          className={styles.authItems}
          data-part={getHamburgerPartProps("auth-items")}
        >
          {canEditCms && (
          <div
            className={styles.authItem}
            data-part={getHamburgerPartProps("auth-item")}
          >
            <img
              src={userCircleIcon}
              alt=""
              className={styles.authItemIcon}
              data-part={getHamburgerPartProps("auth-item-icon")}
            />
            <span
              className={styles.authItemText}
              data-part={getHamburgerPartProps("auth-item-text")}
            >
                    CMS
            </span>
            <div className={styles.authItemToggle}>
              <Switch
                on={cmsEditMode}
                onClick={() => setCmsEditMode(!cmsEditMode)}
                ariaLabel={`CMS edit mode, ${cmsEditMode ? "enabled" : "disabled"}`}
              />
            </div>
          </div>
              )}
          {authItems.map((item) => (
            <button
              type="button"
              key={item.name}
              className={styles.authItem}
              data-part={getHamburgerPartProps("auth-item")}
              onClick={() => {
                    trackNavClick(item.name);
                    handleNavigation(item.path);
                  }}
            >
              <span
                className={styles.authItemIconWrapper}
                data-part={getHamburgerPartProps("auth-item-icon-wrapper")}
              >
                <img
                  src={item.icon}
                  alt=""
                  className={styles.authItemIcon}
                  data-part={getHamburgerPartProps("auth-item-icon")}
                />
                {item.name === "Orders" && hasActiveOrders && (
                <span
                  className={styles.ordersAttentionGrabber}
                  data-part={getHamburgerPartProps(
                          "orders-attention-grabber",
                        )}
                  aria-hidden="true"
                >
                  <img
                    src={ordersBadgeIcon}
                    alt=""
                    className={styles.ordersAttentionGrabberIcon}
                    data-part={getHamburgerPartProps(
                            "orders-attention-grabber-icon",
                          )}
                  />
                </span>
                    )}
              </span>
              <span
                className={styles.authItemText}
                data-part={getHamburgerPartProps("auth-item-text")}
              >
                {item.name}
              </span>
            </button>
              ))}
        </div>
      </>
        )}

      {/* Scan Barcode - shown on mobile for Punchh loyalty users */}
      {!isDesktop && isAuthenticated && activeLoyalty === PUNCHH && (
      <div
        className={styles.authItems}
        data-part={getHamburgerPartProps("auth-items")}
      >
        <button
          type="button"
          className={styles.authItem}
          data-part={getHamburgerPartProps("auth-item")}
          onClick={handleScanBarcode}
        >
          <img
            src={barcodeIcon}
            alt=""
            className={styles.authItemIcon}
            data-part={getHamburgerPartProps("auth-item-icon")}
          />
          <span
            className={styles.authItemText}
            data-part={getHamburgerPartProps("auth-item-text")}
          >
                Scan Barcode
          </span>
        </button>
      </div>
        )}

      {/* Divider between auth items and download app - shown on mobile */}
      {!isDesktop && (
      <div
        className={styles.divider}
        data-part={getHamburgerPartProps("divider")}
      />
        )}

      {/* Download App Button - shown on mobile */}
      {!isDesktop && (
      <a
        href={
              /iPad|iPhone|iPod/.test(navigator.userAgent)
                ? "https://apps.apple.com/us/app/insomnia-cookies/id891379973"
                : "https://play.google.com/store/apps/details?id=com.insomniacookies.insomnia"
            }
        target="_blank"
        rel="noopener noreferrer"
        className={styles.downloadAppButton}
        data-part={getHamburgerPartProps("download-app")}
        aria-label="Download Insomnia Cookies app"
        onClick={() => trackNavClick("Download Insomnia App")}
      >
        {isBrandRefresh ? (
          <span
            data-part={getHamburgerPartProps("download-app-icon-wrap")}
          >
            <img
              src={appDownloadIcon}
              alt=""
              className={styles.downloadAppIcon}
              data-part={getHamburgerPartProps("download-app-icon")}
            />
          </span>
            ) : (
              <img
                src={appDownloadIcon}
                alt=""
                className={styles.downloadAppIcon}
              />
            )}
        <div
          className={styles.downloadAppTextContainer}
          data-part={getHamburgerPartProps("download-app-text")}
        >
          <span
            className={styles.downloadAppTitle}
            data-part={getHamburgerPartProps("download-app-title")}
          >
                Insomnia App
          </span>
          <span
            className={styles.downloadAppSubtitle}
            data-part={getHamburgerPartProps("download-app-subtitle")}
          >
                Download Insomnia App
          </span>
        </div>
      </a>
        )}

      {isDesktop && (
      <div
        className={styles.divider}
        data-part={getHamburgerPartProps("divider")}
      />
        )}

      <div
        className={styles.navItems}
        data-part={getHamburgerPartProps("nav-items")}
      >
        {navItems.map((item) => {
            if (item.isExpandable) {
              return (
                <div
                  key={item.name}
                  className={styles.moreAccordion}
                  data-part={getHamburgerPartProps("more-accordion")}
                >
                  <button
                    type="button"
                    className={styles.navItem}
                    data-part={getHamburgerPartProps("nav-item")}
                    onClick={() => {
                      trackNavClick(item.name);
                      handleMoreClick();
                    }}
                    aria-expanded={isMoreExpanded}
                  >
                    <span
                      className={styles.navItemText}
                      data-part={getHamburgerPartProps("nav-item-text")}
                    >
                      {item.name}
                    </span>
                    <img
                      src={isMoreExpanded ? minusIcon : plusIcon}
                      alt=""
                      className={styles.navItemSuffix}
                      data-part={getHamburgerPartProps("nav-item-suffix")}
                    />
                  </button>
                  {isMoreExpanded && (
                    <div
                      className={styles.moreItems}
                      data-part={getHamburgerPartProps("more-items")}
                    >
                      {moreItems.map((moreItem) => (
                        isExternalPath(moreItem.path) ? (
                          <a
                            key={moreItem.name}
                            href={moreItem.path}
                            className={styles.moreItem}
                            data-part={getHamburgerPartProps("more-item")}
                            onClick={() => {
                              trackNavClick(moreItem.name);
                              toggleMobileMenu();
                            }}
                          >
                            {moreItem.name}
                          </a>
                        ) : (
                          <button
                            type="button"
                            key={moreItem.name}
                            className={styles.moreItem}
                            data-part={getHamburgerPartProps("more-item")}
                            onClick={() => {
                              trackNavClick(moreItem.name);
                              handleNavigation(moreItem.path);
                            }}
                          >
                            {moreItem.name}
                          </button>
                        )
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            if (isExternalPath(item.path)) {
              return (
                <a
                  key={item.name}
                  href={item.path}
                  className={styles.navItem}
                  data-part={getHamburgerPartProps("nav-item")}
                  onClick={() => {
                    trackNavClick(item.name);
                    toggleMobileMenu();
                  }}
                >
                  <span
                    className={styles.navItemText}
                    data-part={getHamburgerPartProps("nav-item-text")}
                  >
                    {item.name}
                  </span>
                </a>
              );
            }

            return (
              <button
                type="button"
                key={item.name}
                className={styles.navItem}
                data-part={getHamburgerPartProps("nav-item")}
                onClick={() => {
                  trackNavClick(item.name);
                  handleNavigation(item.path);
                }}
              >
                <span
                  className={styles.navItemText}
                  data-part={getHamburgerPartProps("nav-item-text")}
                >
                  {item.name}
                </span>
              </button>
            );
          })}
      </div>

      {/* Social Icons */}
      <div
        className={styles.socialIcons}
        data-part={getHamburgerPartProps("social-icons")}
      >
        {socialIcons.map((social) => {
            if (social.isCookies) {
              return (
                <span
                  key={social.name}
                  id="cookie-settings"
                  className={`${styles.socialIcon} cursor-pointer`}
                  data-part={getHamburgerPartProps("social-icon")}
                  role="button"
                  tabIndex={0}
                  aria-label={social.name}
                  onClick={() => handleSocialIconClick(social)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSocialIconClick(social);
                    }
                  }}
                >
                  <img src={social.icon} alt={social.name} />
                </span>
              );
            }
            if (social.isAccessibility) {
              return (
                <span
                  key={social.name}
                  className={`${styles.socialIcon} cursor-pointer`}
                  data-part={getHamburgerPartProps("social-icon")}
                  role="button"
                  tabIndex={0}
                  aria-label={social.name}
                  onClick={() => handleSocialIconClick(social)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSocialIconClick(social);
                    }
                  }}
                >
                  <img src={social.icon} alt={social.name} />
                </span>
              );
            }
            if (social.isLink) {
              return (
                <a
                  key={social.name}
                  href={social.path}
                  className={styles.socialIcon}
                  data-part={getHamburgerPartProps("social-icon")}
                  aria-label={social.name}
                >
                  <img src={social.icon} alt={social.name} />
                </a>
              );
            }
            return (
              <a
                key={social.name}
                href={social.path}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialIcon}
                data-part={getHamburgerPartProps("social-icon")}
                aria-label={social.name}
              >
                <img src={social.icon} alt={social.name} />
              </a>
            );
          })}
      </div>

      {/* Legal Footer */}
      <div
        className={styles.legalFooter}
        data-part={getHamburgerPartProps("legal-footer")}
      >
        <p
          className={styles.legalText}
          data-part={getHamburgerPartProps("legal-text")}
        >
            This site is protected by reCAPTCHA and the Google
          {" "}
          <a href="https://policies.google.com/privacy">Privacy Policy</a>
          {" "}
            and
          {" "}
          <a href="https://policies.google.com/terms">Terms of Service</a>
          {" "}
            apply.
        </p>
        <p
          className={styles.copyrightText}
          data-part={getHamburgerPartProps("copyright-text")}
        >
            insomnia cookies warm.delicious.delivered. ©
          {' '}
          {currentYear}
.
        </p>
      </div>
    </>
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className={getOverlayClassName()}
      onClick={handleOverlayClick}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          handleClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
      tabIndex={-1}
    >
      <div
        className={getContainerClassName()}
        data-region={getHamburgerRegionProps()}
        data-part={getHamburgerPartProps("menu-container")}
      >
        {isBrandRefresh ? (
          <div
            className={styles.menuContent}
            data-part={getHamburgerPartProps("container")}
          >
            {menuBody}
          </div>
        ) : (
          menuBody
        )}
      </div>
    </div>
  );
};

HamburgerMenu.propTypes = {
  toggleMobileMenu: PropTypes.func.isRequired,
  toggleBarcodeCard: PropTypes.func.isRequired,
  userToken: PropTypes.string,
  permissions: PropTypes.arrayOf(PropTypes.shape({})),
  cmsEditMode: PropTypes.bool,
  setCmsEditMode: PropTypes.func.isRequired,
  activeLoyalty: PropTypes.string,
  footerMenuItemsSettings: PropTypes.shape({}),
  footerMenuItemsLinksSettings: PropTypes.shape({}),
  hasActiveOrders: PropTypes.bool,
  selectedCountry: PropTypes.shape({
    short_name: PropTypes.string,
  }),
};

HamburgerMenu.defaultProps = {
  userToken: null,
  permissions: [],
  cmsEditMode: false,
  activeLoyalty: null,
  footerMenuItemsSettings: {},
  footerMenuItemsLinksSettings: {},
  hasActiveOrders: false,
  selectedCountry: null,
};

const mapStateToProps = (state) => ({
  userToken: state.user.userToken,
  permissions: state.user.permissions || [],
  cmsEditMode: state.user.cmsEditMode,
  activeLoyalty: state.loyalty?.activeLoyalty || null,
  footerMenuItemsSettings: state.footerSettings?.footerMenuItemsSettings || {},
  footerMenuItemsLinksSettings:
    state.footerSettings?.footerMenuItemsLinksSettings || {},
  hasActiveOrders: state.elements?.hasActiveOrders || false,
  selectedCountry: state.country?.selectedCountry || null,
});

const mapDispatchToProps = (dispatch) => ({
  toggleMobileMenu: () => dispatch(elementsActions.toggleMobileMenu()),
  toggleBarcodeCard: (value) => dispatch(elementsActions.toggleBarcodeCard(value)),
  setCmsEditMode: (value) => dispatch(userActions.setCmsEditMode(value)),
});

export { mapStateToProps, mapDispatchToProps };

export default connect(mapStateToProps, mapDispatchToProps)(HamburgerMenu);
